import { Download, FileDown, Pencil, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    type CatalogBulkPatch,
    type CatalogItemFilters,
    bulkUpdateItems,
    countItems,
    exportCatalog,
    exportCatalogSelected,
    generateMissingQuestions,
    getCatalogItem,
    getCatalogSummary,
    getCompleteness,
    importCatalog,
    listCatalogItems,
    listParts,
} from '~/api/catalog'
import type {
    CatalogCompleteness,
    CatalogItem,
    CatalogSummary,
    PartCount,
} from '~/api/types'
import { CatalogItemEditor } from '~/components/CatalogItemEditor'
import { BulkEditDialog } from '~/components/catalog/BulkEditDialog'
import { CompletenessPanel } from '~/components/catalog/CompletenessPanel'
import { FilterPanel } from '~/components/catalog/FilterPanel'
import { ItemTable } from '~/components/catalog/ItemTable'
import { SummaryStrip } from '~/components/catalog/SummaryStrip'
import { RevisionDiffPanel } from '~/components/RevisionDiffPanel'
import { BulkActionBar } from '~/components/ui/bulk-action-bar'
import { Button } from '~/components/ui/button'
import { useToast } from '~/components/ui/toast'
import { useTableSelection } from '~/lib/use-table-selection'
import { useAuthStore } from '~/stores/auth-store'

// 콘텐츠 > 카탈로그 — 관리자 워크벤치. 진입→찾기(검색·다중 필터)→편집(드로어)→
// 개정 반영(diff 후 선택 일괄 반영). 935개 항목을 고밀도로 다룬다(KRDS: 명료·절제).
// 단건 편집/추가는 여기서, 전체 매뉴얼 재적재는 운영 CLI(datapipe) 책임.

const PAGE_SIZE = 50

const EMPTY_FILTERS: CatalogItemFilters = {}

export function CatalogPage() {
    const toast = useToast()
    const [searchParams, setSearchParams] = useSearchParams()
    const [summary, setSummary] = useState<CatalogSummary | null>(null)
    const [completeness, setCompleteness] =
        useState<CatalogCompleteness | null>(null)
    const [parts, setParts] = useState<PartCount[]>([])
    // 빈 질의 생성은 superadmin·LLM 작업이라 권한 게이트 + 진행 중 중복클릭 방지.
    const isSuperadmin = useAuthStore((s) => s.adminRole) === 'superadmin'
    const [generatingQuestions, setGeneratingQuestions] = useState(false)

    const [filters, setFilters] = useState<CatalogItemFilters>(EMPTY_FILTERS)
    const [offset, setOffset] = useState(0)

    const [items, setItems] = useState<CatalogItem[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)

    // 편집 드로어: CatalogItem이면 수정, null이면 '새 항목', undefined면 닫힘.
    const [editing, setEditing] = useState<CatalogItem | null | undefined>(
        undefined
    )
    // 편집기 폼 재초기화 키. 단건 GET으로 최신값이 도착하면 증가시켜 폼을 새 값으로 다시 채운다.
    const [editorKey, setEditorKey] = useState(0)
    const [diffOpen, setDiffOpen] = useState(false)
    const [reindexHint, setReindexHint] = useState(false)

    const [refreshKey, setRefreshKey] = useState(0)

    // 엑셀 다운로드/업로드 진행 상태와 숨긴 파일 입력 ref.
    const [exporting, setExporting] = useState(false)
    const [importing, setImporting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // 다중선택(범용 훅). 페이지 이동·필터 변경에도 선택을 유지한다.
    const selection = useTableSelection<string>()
    const [bulkExporting, setBulkExporting] = useState(false)
    const [bulkEditOpen, setBulkEditOpen] = useState(false)
    const [bulkUpdating, setBulkUpdating] = useState(false)

    // 요약·PART는 화면 진입/갱신 시 1회. (PART 칩은 디바운스 없이 즉시.)
    useEffect(() => {
        getCatalogSummary()
            .then(setSummary)
            .catch(() => setSummary(null))
        getCompleteness()
            .then(setCompleteness)
            .catch(() => setCompleteness(null))
        listParts()
            .then(setParts)
            .catch(() => setParts([]))
    }, [refreshKey])

    // 목록 + 개수(동일 필터). 필터/offset/갱신 변화에 반응한다.
    useEffect(() => {
        setLoading(true)
        Promise.all([
            listCatalogItems({ ...filters, limit: PAGE_SIZE, offset }),
            countItems(filters),
        ])
            .then(([rows, c]) => {
                setItems(rows)
                setTotal(c.total)
            })
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setLoading(false))
    }, [filters, offset, refreshKey, toast])

    // ⌘K 팔레트가 /content/catalog?highlight=<id>로 이동하면 단건 조회 후 편집 드로어를
    // 자동으로 연다. 처리 후 쿼리파라미터는 제거(replace)해 새로고침·뒤로가기 시 재발화 방지.
    useEffect(() => {
        const highlight = searchParams.get('highlight')
        if (!highlight) return
        setSearchParams(
            (prev) => {
                prev.delete('highlight')
                return prev
            },
            { replace: true }
        )
        getCatalogItem(highlight)
            .then((full) => {
                setEditing(full)
                setEditorKey((k) => k + 1)
            })
            .catch((e) => toast.error((e as Error).message))
    }, [searchParams, setSearchParams, toast])

    // 필터 부분 갱신 → offset 0으로 리셋.
    const onFilterChange = (patch: Partial<CatalogItemFilters>) => {
        setFilters((f) => ({ ...f, ...patch }))
        setOffset(0)
    }
    const onResetFilters = () => {
        setFilters(EMPTY_FILTERS)
        setOffset(0)
    }

    // 행 클릭 → 단건 GET으로 최신 로드 후 편집기 오픈(목록에는 긴 텍스트가 없을 수 있음).
    const onSelectItem = (item: CatalogItem) => {
        setEditing(item) // 즉시 열어 응답성 확보(폼은 목록 값으로 선표시)
        setEditorKey((k) => k + 1)
        getCatalogItem(item.id)
            .then((full) => {
                setEditing(full)
                setEditorKey((k) => k + 1) // 최신값 도착 → 폼 재초기화
            })
            .catch(() => {
                /* 실패 시 목록 값 유지 */
            })
    }

    const refresh = () => setRefreshKey((k) => k + 1)

    const onSaved = (message: string) => {
        setEditing(undefined)
        refresh()
        toast.success(message)
    }

    // 개정본 일괄 반영 성공 → 목록·요약 갱신 + 재색인 안내 배너.
    const onApplied = (message: string) => {
        setDiffOpen(false)
        refresh()
        setReindexHint(true)
        toast.success(message, {
            action: { label: '목록 새로고침', onClick: refresh },
        })
    }

    // 엑셀 다운로드: JWT 인증이 필요해 blob fetch로 받아 임시 <a download>로 저장한다.
    const onExport = async () => {
        setExporting(true)
        try {
            await exportCatalog()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setExporting(false)
        }
    }

    // 엑셀 업로드: 선택한 .xlsx를 import → 결과 토스트 + 목록·요약 갱신. 권한/형식 오류는 토스트.
    const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // 같은 파일 재선택도 onChange가 발화하도록 초기화
        if (!file) return
        setImporting(true)
        try {
            const r = await importCatalog(file)
            refresh()
            toast.success(
                `엑셀 반영 완료 · 수정 ${r.updated} · 신규 ${r.created} · 실패 ${r.failed.length}`,
                { action: { label: '목록 새로고침', onClick: refresh } }
            )
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setImporting(false)
        }
    }

    // 선택 내보내기: 선택한 id만 .xlsx로(POST 본문에 ids). 인증 위해 blob fetch.
    const onExportSelected = async () => {
        const ids = [...selection.selected]
        if (ids.length === 0) return
        setBulkExporting(true)
        try {
            await exportCatalogSelected(ids)
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBulkExporting(false)
        }
    }

    // 빈 사전 질의 LLM 일괄 생성(superadmin) → 결과 토스트 + 완전성·목록 새로고침. 수 분 소요 가능.
    const onGenerateQuestions = async () => {
        if (generatingQuestions) return // 진행 중 중복 호출 방지
        setGeneratingQuestions(true)
        try {
            const r = await generateMissingQuestions()
            refresh()
            toast.success(
                `생성 ${r.generated} · 실패 ${r.failed} · 남음 ${r.remaining}`,
                { action: { label: '목록 새로고침', onClick: refresh } }
            )
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setGeneratingQuestions(false)
        }
    }

    // 일괄 편집: 선택 ids에 patch 한 필드 적용 → 결과 토스트 + 목록·요약 새로고침 + 선택 해제.
    const onBulkUpdate = async (patch: CatalogBulkPatch) => {
        const ids = [...selection.selected]
        if (ids.length === 0) return
        setBulkUpdating(true)
        try {
            const r = await bulkUpdateItems(ids, patch)
            setBulkEditOpen(false)
            selection.clear()
            refresh()
            toast.success(
                `일괄 편집 완료 · 수정 ${r.updated} · 실패 ${r.failed.length}`,
                { action: { label: '목록 새로고침', onClick: refresh } }
            )
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBulkUpdating(false)
        }
    }

    return (
        <div>
            {/* 헤더 바 */}
            <div className='mb-5 flex items-start justify-between gap-4'>
                <div>
                    <h1 className='krds-heading-m text-foreground'>
                        체크리스트 카탈로그
                    </h1>
                    <p className='text-muted-foreground mt-1.5 text-sm'>
                        2025 매뉴얼 기준 · 평가 항목 검색·편집·개정 반영
                    </p>
                    <p className='text-muted-foreground/80 mt-1 text-[13px]'>
                        엑셀로 오프라인 편집 후 업로드하면 <strong>ID</strong>로
                        매칭해 반영해요. ID 컬럼은 <strong>수정 금지</strong>
                        (매칭 키), 비우면 신규로 추가됩니다.
                    </p>
                </div>
                <div className='mt-1 flex shrink-0 items-center gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={onExport}
                        disabled={exporting}
                        title='전체 카탈로그를 .xlsx로 받아요. ID 컬럼은 매칭 키라 수정하지 마세요.'>
                        <Download />
                        {exporting ? '내려받는 중…' : '엑셀 다운로드'}
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                        title='편집한 .xlsx를 올려 ID로 매칭 반영해요. ID를 비우면 신규로 추가돼요(superadmin).'>
                        <Upload />
                        {importing ? '반영하는 중…' : '엑셀 업로드'}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.xlsx'
                        className='hidden'
                        onChange={onImportFile}
                    />
                    <Button variant='outline' onClick={() => setDiffOpen(true)}>
                        개정본 비교
                    </Button>
                    <Button
                        onClick={() => {
                            setEditing(null)
                            setEditorKey((k) => k + 1)
                        }}>
                        + 새 항목
                    </Button>
                </div>
            </div>

            {reindexHint ? (
                <div className='bg-info-subtle border-info/30 text-info mb-4 flex items-center justify-between gap-3 rounded-[var(--radius)] border px-4 py-3 text-sm'>
                    <span>
                        개정 내용을 반영했어요. 검색·평가 정합성을 위해 운영
                        CLI(datapipe)에서 <strong>재색인</strong>을 진행하세요.
                    </span>
                    <button
                        type='button'
                        onClick={() => setReindexHint(false)}
                        className='shrink-0 text-xs font-bold underline'>
                        닫기
                    </button>
                </div>
            ) : null}

            <SummaryStrip summary={summary} />

            <CompletenessPanel
                completeness={completeness}
                canGenerate={isSuperadmin}
                generating={generatingQuestions}
                onGenerate={onGenerateQuestions}
            />

            {/* 본문 2단 */}
            <div className='grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]'>
                <FilterPanel
                    filters={filters}
                    onChange={onFilterChange}
                    onReset={onResetFilters}
                    parts={parts}
                    total={total}
                />

                <div className='space-y-3'>
                    <BulkActionBar
                        count={selection.count}
                        onClear={selection.clear}
                        sticky
                        position='top'>
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={onExportSelected}
                            disabled={bulkExporting}>
                            <FileDown />
                            {bulkExporting ? '내보내는 중…' : '선택 내보내기'}
                        </Button>
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setBulkEditOpen(true)}>
                            <Pencil />
                            일괄 편집
                        </Button>
                    </BulkActionBar>
                    <ItemTable
                        items={items}
                        loading={loading}
                        total={total}
                        offset={offset}
                        pageSize={PAGE_SIZE}
                        selectedId={editing?.id}
                        selection={selection}
                        onSelect={onSelectItem}
                        onOffset={setOffset}
                    />
                </div>
            </div>

            {editing !== undefined ? (
                <CatalogItemEditor
                    key={editorKey}
                    item={editing}
                    onClose={() => setEditing(undefined)}
                    onSaved={onSaved}
                />
            ) : null}

            {diffOpen ? (
                <RevisionDiffPanel
                    onClose={() => setDiffOpen(false)}
                    onApplied={onApplied}
                />
            ) : null}

            {bulkEditOpen ? (
                <BulkEditDialog
                    count={selection.count}
                    busy={bulkUpdating}
                    onApply={onBulkUpdate}
                    onClose={() => setBulkEditOpen(false)}
                />
            ) : null}
        </div>
    )
}
